import type { ConnectionPhase } from '@parsleyai/ui'
import { collection, type DocumentData, doc, onSnapshot, type QuerySnapshot, setDoc } from 'firebase/firestore'
import { createContext, type ReactNode, useCallback, useEffect, useReducer } from 'react'
import { releases } from '@/content/whats-new/releases'
import { db } from '@/services/firebase/config'
import { useLiveSubscription } from '@/store/userData/useLiveSubscription'
import { useUserData } from '@/store/userData/useUserData'
import { initialPreferencesState, preferencesReducer } from './preferencesReducer'
import {
  type HomePreferences,
  type LibraryPreferences,
  type MealPlanPreferences,
  PreferenceDoc,
  type PreferenceDocShapes,
  PreferencesActionTypes,
  type PreferencesSettings,
  type PreferencesState,
  type PrivacyPreferences
} from './types'

export interface PreferencesContextValue {
  state: PreferencesState
  /** Merge a patch into a single preference doc (create-or-update). */
  updatePreferences: <K extends PreferenceDoc>(docId: K, patch: Partial<PreferenceDocShapes[K]>) => Promise<void>
  /** Convenience wrapper over updatePreferences: persist the last meal plan the user viewed. */
  updateMealPlanPreference: (planId: string) => Promise<void>
  /** Live-connection phase for the subscription (auto-resubscribes on drop; banner-ready). */
  phase: ConnectionPhase
  /** Manual resubscribe. */
  retry: () => void
}

export const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined)

interface PreferencesProviderProps {
  children: ReactNode
}

export function PreferencesProvider({ children }: PreferencesProviderProps) {
  const { uid } = useUserData()
  const [state, dispatch] = useReducer(preferencesReducer, initialPreferencesState)

  // Drop cached prefs on sign-out; the subscription itself is disabled by the null key below.
  useEffect(() => {
    if (!uid) dispatch({ type: PreferencesActionTypes.CLEAR })
  }, [uid])

  const { phase, retry } = useLiveSubscription<QuerySnapshot<DocumentData>>({
    subscriptionKey: uid,
    label: 'preferences',
    subscribe: (activeUid, { onData, onError }) => onSnapshot(collection(db, 'users', activeUid, 'preferences'), onData, onError),
    onData: (snapshot) => {
      // Materialize the known preference docs out of the collection. Any unknown doc id is
      // ignored; an absent known doc stays null — not an error. `privacy` is the cross-device
      // mirror consumed by AnalyticsConsentContext (which owns the boot + SDK behavior).
      let mealPlans: MealPlanPreferences | null = null
      let settings: PreferencesSettings | null = null
      let privacy: PrivacyPreferences | null = null
      let library: LibraryPreferences | null = null
      let home: HomePreferences | null = null
      for (const docSnap of snapshot.docs) {
        if (docSnap.id === PreferenceDoc.MealPlans) mealPlans = docSnap.data() as MealPlanPreferences
        else if (docSnap.id === PreferenceDoc.Settings) settings = docSnap.data() as PreferencesSettings
        else if (docSnap.id === PreferenceDoc.Privacy) privacy = docSnap.data() as PrivacyPreferences
        else if (docSnap.id === PreferenceDoc.Library) library = docSnap.data() as LibraryPreferences
        else if (docSnap.id === PreferenceDoc.Home) home = docSnap.data() as HomePreferences
      }
      dispatch({ type: PreferencesActionTypes.SET, payload: { mealPlans, settings, privacy, library, home } })
    }
  })

  const updatePreferences = useCallback(
    async <K extends PreferenceDoc>(docId: K, patch: Partial<PreferenceDocShapes[K]>) => {
      if (!uid) return
      await setDoc(doc(db, 'users', uid, 'preferences', docId), patch, { merge: true })
    },
    [uid]
  )

  const updateMealPlanPreference = useCallback(
    (planId: string) => updatePreferences(PreferenceDoc.MealPlans, { lastViewedPlanId: planId }),
    [updatePreferences]
  )

  // Mirror the browser's zone so server-rendered surfaces (email) can date things in the user's
  // own day. Waits for the snapshot so a first load doesn't write over a zone we haven't read
  // yet, and only writes on an actual change — travel, a new device, or the very first sign-in.
  const storedTimeZone = state.settings?.timeZone
  useEffect(() => {
    if (!uid || state.loading) return
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!browserTimeZone || browserTimeZone === storedTimeZone) return
    void updatePreferences(PreferenceDoc.Settings, { timeZone: browserTimeZone })
  }, [uid, state.loading, storedTimeZone, updatePreferences])

  // Seed the release-log watermark on first read. A user with no watermark isn't behind on
  // anything, so they get no unseen dot until the next entry ships. Same snapshot guard as above.
  const whatsNewLastSeen = state.settings?.whatsNewLastSeen
  useEffect(() => {
    if (!uid || state.loading || whatsNewLastSeen) return
    void updatePreferences(PreferenceDoc.Settings, { whatsNewLastSeen: releases[0].shipDate })
  }, [uid, state.loading, whatsNewLastSeen, updatePreferences])

  return (
    <PreferencesContext.Provider value={{ state, updatePreferences, updateMealPlanPreference, phase, retry }}>
      {children}
    </PreferencesContext.Provider>
  )
}
